import { Comedian } from "./comedian";
import { FrontendUsage, Usage } from "./promotions";
import { VenuePagesRow } from "./venue_pages";
import { VenueRow } from "./venues";
import { logException } from "@punchup/sentry";

export interface GetTixologiEventsParams {
  ids?: string[];
  perPage?: number;
  page?: number;
  date?: string;
  search?: string;
  partner_id?: number;
  token?: string;
  // Bounds the upstream fetch (e.g. AbortSignal.timeout) so a hung Tixologi
  // call fails into the caller's error path instead of the platform timeout.
  signal?: AbortSignal;
}

/**
 * One comedian in a show's lineup, as pushed to the tixologi iframe's Show
 * lineup tile (PUN-4942, `comedians-updated`).
 *
 * A deliberate projection of the `comedians` row rather than the row itself:
 * public display data only, so the payload carries nothing private across the
 * origin boundary and a `gen_types` run can never silently reshape the
 * cross-repo contract. Array order is lineup order (`show_comedian_pairings.ordering`).
 */
export interface TixologiLineupComedian {
  /** `comedians.id`. */
  id: string;
  /** `comedians.display_name` — the name the host's Comedians section renders. */
  name: string;
  /**
   * A ready-to-render CDN URL, or null when the comedian has no avatar. The host
   * resolves it, because `comedians.about_img` stores a Cloudflare image id, not
   * a URL — sending the raw column would render nothing in the iframe.
   */
  imageUrl: string | null;
}

export interface TixologiIframeProps {
  url?: string;
  comedian?: Comedian;
  venuePage: VenuePagesRow | null;
  venue?: VenueRow;
  showId?: string;
  /** Live show title — the venue-page slug source for Copy link (`/shows/{slugified title}`). */
  showTitle?: string | null;
  /** The public URL of the venue page that will list this show, decided by the
   *  mounting server page; null when no venue page will, and Copy link falls back. */
  venuePageUrl?: string | null;
  isComedian?: boolean;
  onEventCreated?: (eventId: string) => void;
  onEventUpdated?: (eventId: string) => void;
  onEventDeleted?: (showId?: string) => void;
  onEventDuplicated?: (eventId: string, showId?: string) => void;
  onDownloadEventQr?: (eventId: string) => void;
  onEventEditClicked?: (eventId: string) => void;
  onSendCompedTickets?: () => void;
  onCancel?: () => void;
  /**
   * Called when a tixologi sub-page becomes active or exits.
   * `pageName` is the current sub-page name, or `null` when returning to the dashboard.
   * `parentPageName` is an optional intermediate crumb (e.g. "Comped Tickets" when
   * "Send Tickets" is nested inside it). Omitted by pages with no parent crumb.
   * `parentRoute` is the tixologi route for the parent sub-page, used to navigate back
   * to it when the intermediate crumb is clicked.
   */
  onSubPageChange?: (
    pageName: string | null,
    parentPageName?: string | null,
    parentRoute?: string | null
  ) => void;
  /**
   * Called every time tixologi's `dashboard-ready` postMessage is received (including on
   * remounts after sub-page navigation). `isShowDashboard` is `true` when the message came
   * from `PunchupDashboard` — use it to show the comedian accordion. `false` when any other
   * displayable page fires it (currently only PunchupDashboard sets this flag).
   *
   * NOTE: fires when event *data* is loaded, which may be before content is visually present.
   * Prefer `onIframeContentReady` for hiding/showing loading overlays.
   */
  onDashboardReady?: (isShowDashboard: boolean) => void;
  /**
   * Called when a tixologi sub-page fires `page-ready` on mount. Signals that a displayable
   * page has loaded — use this to cancel any pending loading-cover debounce started by
   * `onPageLeave` so the overlay does not flash during fast sub-page navigation.
   */
  onPageReady?: () => void;
  /**
   * Called when the active tixologi page fires `page-leave` on unmount. Triggers a debounced
   * loading cover on the host side (default 200 ms). If `onPageReady` or `onDashboardReady`
   * arrives before the debounce fires, the cover is cancelled and the user sees no flash.
   * If the debounce fires, the cover appears — covering slow or unexpected tixologi redirects.
   */
  onPageLeave?: () => void;
  /**
   * Called once when the iframe is visually ready to be displayed — the combined signal
   * that both the height gate (> IFRAME_MIN_CONTENT_HEIGHT) and the Punchup handshake
   * gate have been satisfied. Use this (not onDashboardReady) to remove loading overlays
   * and show content below the iframe.
   *
   * For new tixologi the handshake gate is `dashboard-ready`. For legacy tixologi that
   * never sends `dashboard-ready`, a 1 s fallback timer fires after height crosses the
   * threshold — this is preceded by onDashboardReady(false) so the host can activate
   * legacy-mode comedian visibility before the overlay drops.
   */
  onIframeContentReady?: () => void;
  /** Called when the user clicks "Expand all" inside the tixologi accordion group.
   *  Use to expand any parent-frame accordions (e.g. comedian accordion) in sync. */
  onAccordionExpandAll?: () => void;
  /** Called when the user clicks "Collapse all" inside the tixologi accordion group.
   *  Use to collapse any parent-frame accordions in sync. */
  onAccordionCollapseAll?: () => void;
  /** Called when the iframe asks for the lineup (`request-comedians`), which its Show lineup
   *  tile posts on mount. The host pushes `comedians-updated` on `dashboard-ready` and on every
   *  lineup edit, but a tile that mounts later than `dashboard-ready` would otherwise sit empty
   *  until an unrelated refresh. Mirrors `request-viewport-offset` -> `parent-scroll`. (PUN-4942) */
  onRequestComedians?: () => void;
  /** Called when the user clicks the iframe's Show lineup tile (`scroll-to-comedians`).
   *  The lineup lives in the host, below the iframe, so the host must open its own
   *  Comedians section and scroll to it — waiting for the accordion's expand
   *  transition to settle, or the scroll lands on the pre-expand height. (PUN-4942) */
  onScrollToComedians?: () => void;
  /** Called when the event name is updated via GeneralSection. Use to update breadcrumb
   *  display names optimistically without a full page refresh. */
  onEventNameUpdated?: (name: string) => void;
  /** When true, suppresses the minHeight calc that fills the viewport. Pass when content below the
   *  iframe (e.g. comedian accordion) should sit directly under the tixologi content, not a
   *  viewport-height gap below it. */
  disableMinHeight?: boolean;
  /**
   * Called when tixologi fires a navigation-guard postMessage — i.e. the user has attempted
   * to navigate away with unsaved form changes. The parent must show a confirmation dialog,
   * then call respond(true) to allow navigation or respond(false) to cancel it.
   * If not provided, navigation is automatically allowed (confirmed: true) so BrowserRouter
   * does not hang waiting for a response.
   */
  onNavigationGuard?: (
    message: string,
    respond: (confirmed: boolean) => void
  ) => void;
  /**
   * Called when the iframe element fires its native `load` event — i.e. the tixologi SPA has
   * (re)loaded. Use this to reset any loading-overlay state so the parent shows the overlay
   * again until the next `onIframeContentReady` fires.
   *
   * This fires on the initial load AND on every subsequent hard reload (e.g. after a
   * redeployment). It does NOT fire for SPA-internal navigation (history.pushState).
   */
  onIframeReload?: () => void;
}

export interface TixologiDay {
  id: number;
  start_date: string;
  end_date: string;
  quantity: number;
  sold: number;
}

export interface TixologiEventWebpageSettings {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;
  event_id: number;
  active_on_widget: boolean;
  selling_allowed: boolean;
  transfering_allowed: boolean;
  default_theme_colors_style: boolean;
  background_color: string;
  theme_color: string;
  header_color: string;
  family_font: string;
  default_typography_style: boolean;
  header_brand_color: string;
  header_font_style: string;
  title_brand_color: string;
  title_font_style: string;
  sub_title_brand_color: string;
  sub_title_font_style: string;
  text_brand_color: string;
  text_font_style: string;
  callouts_brand_color: string;
  callouts_font_style: string;
  event_banner: string;
  banner_height: number;
  banner_height_mobile: number;
  image_position: string;
  guest_checkout_enabled: boolean;
  apple_pay_enabled: boolean;
}

export interface TixologiTicketType {
  ID: number;
  name: string;
  sold: number;
  sold_out: boolean;
  amount: number;
  holds_amount: number;
  initial_price: number;
  /** Hex swatch the organiser picked, absent on types created before colours. */
  color?: string | null;
  color_name?: string | null;
  image: string;
  selling_time: string;
  off_selling_time: string;
  ticket_visibility: boolean;
  buyer_absorb_payment_fee: boolean;
  buyer_absorb_tixologist_fee: boolean;
  description: string;
  secondary_royalty: number;
  allow_secondary_sales: boolean;
  // template: TixologiTemplate;
  event_id: number;
  event_name: string;
  event_start_time: string;
  order_index: number;
  is_two_for_one: boolean;
  bundle_size: number;
  is_lottery: boolean;
  tickets_pool_id: number;
  tickets_pool: TixologiTicketsPool;
  resumed_event: TixologiResumedEvent;
  fixed_fee?: number;
  percentage_fee?: number;
  pay_what_you_want: boolean;
  min_price?: number;
  max_price?: number;
  seats_category_key?: string;
}

export interface TixologiTicketsPool {
  ID: number;
  name: string;
  description: string;
  total_amount: number;
  sold: number;
  created_at: string;
  updated_at: string;
}

export interface TixologiEventImage {
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string | null;
  ID: number;
  event_id: number;
  image: string; // the URL of the image
  main_image: boolean;
  position: number;
}

export interface TixologiResumedEvent {
  id: number;
  name: string;
  partner_id: number;
  image: string;
  images: TixologiEventImage[];
  media: string[];
  street_address: string;
  venue: string;
  start_date: string;
  end_date: string;
  description: string;
  ticket_types?: TixologiTicketType[];
  tickets_sold: number;
  total_event_budget: string;
  selling_time: string;
  recurrence_id: number;
  start_date_id: number;
  end_date_id: number;
  is_slot: boolean;
  facebook_link: string;
  twitter_link: string;
  instagram_link: string;
  event_webpage_settings: TixologiEventWebpageSettings;
  status: string;
  days: TixologiDay[];
  gate_time: string;
  time_zone: string;
  secondary_time_zone: string;
  live_on_webpage: boolean;
  live_on_widget: boolean;
  // Admin-only switch (mirrors ticketing-api Event.inactive). When true the event is
  // hidden from every public surface and cannot be purchased, but stays visible to admin
  // reads like /events and /punchup/events (which return ResumedEvent, carrying this flag).
  inactive?: boolean;
  sponsors: string;
  postal_code: string;
  secondary_postal_code: string;
  ticketing_mode: string;
  custom_email_message: string;
  custom_fees: string;
  support_contact: string;
  mode: string;
  quantity: number;
  approval_required: boolean;
  door_time: string;
  pixel_id: string;
  gtm_id: string;
  tags?: Tag[];
  is_password_protected?: boolean;
  seats_chart_key?: string;
  seats_event_key?: string;
}

export interface APIEvent {
  ID: string;
  name: string;
  start_date: string;
  end_date: string;
  venue: string;
}

export interface APITicketType {
  ID: string;
  name: string;
}

export interface APICode {
  ID: string;
  code: string;
  link: string;
}

export interface TixologiPromotionResponse {
  promotion: {
    ID: number;
    CreatedAt: string;
    UpdatedAt: string;
    DeletedAt: string | null;
    name: string;
    description: string;
    discount_type: "flat_rate" | "percent";
    status: "active" | "inactive";
    start_date: string;
    end_date?: string | null;
    amount: number;
    applied: "event_specific" | "ticket_specific" | "tag_specific" | "global";
    codes: APICode[];
    rules: any[];
    usages: Usage[];
  };
  usages: FrontendUsage[];
  events: TixologiResumedEvent[];
  ticket_types: TixologiTicketType[];
}

export interface Tag {
  // Tixologi's Go backend serializes the tag primary key inconsistently:
  // - GET /tags returns lowercase `id`
  // - GET /events and /public/ticketing/events embed tags using Go-default
  //   casing (`ID`)
  // `getTixologiEvents` normalizes embedded tags so `id` is always populated;
  // `ID` is preserved here only so the raw shape from the events endpoints is
  // typed and discoverable. Prefer `id` in consumer code.
  id: number;
  ID?: number;
  name: string;
  description?: string;
  tag_type?: "event" | "ticket_type"; // Distinguish between event tags and ticket type tags.
  CreatedAt?: string;
  UpdatedAt?: string;
}

/** One page of upstream events, plus the size of the full result set. */
export interface TixologiEventsPage {
  events: TixologiResumedEvent[];
  /**
   * Total matching events upstream, from `x-total-count`. Null separates "no
   * more pages" from "the upstream did not say".
   */
  total: number | null;
}

/**
 * One page of events with the upstream's total count. `getTixologiEvents` is
 * the same call without it, kept so its many row-only callers do not have to
 * widen their return type.
 */
export const getTixologiEventsPage = async (
  params: GetTixologiEventsParams
): Promise<TixologiEventsPage> => {
  const searchParams = new URLSearchParams({ include_tags: "true" });
  params.ids && searchParams.set("ids", params.ids.join(","));
  params.perPage && searchParams.set("per_page", params.perPage.toString());
  params.page && searchParams.set("page", params.page.toString());
  params.search && searchParams.set("search", params.search);
  params.date && searchParams.set("date", params.date);
  params.partner_id &&
    searchParams.set("partner_id", params.partner_id.toString());

  const path = params.token ? "/events" : "/public/ticketing/events";
  const headers = params.token
    ? { Authorization: `Bearer ${params.token}` }
    : undefined;

  const response = await fetch(
    `${process.env.NEXT_PUBLIC_TIXOLOGI_API_URL}${path}?${searchParams.toString()}`,
    { headers, signal: params.signal }
  );
  const body = await response.json();

  // Tixologi returns an array on success and a `{error}`-style object (or
  // null) on failure. Without this guard, the non-array body cascades into
  // 500s downstream when callers do `.find()` / `.map()` over the result.
  if (!Array.isArray(body)) {
    logException(new Error("Tixologi /events returned a non-array response"), {
      extra: {
        status: response.status,
        body,
        ids_count: params.ids?.length,
      },
    });
    return { events: [], total: null };
  }

  const events: TixologiResumedEvent[] = body;

  // Normalize embedded tags so `id` (lowercase) is always populated — see the
  // `Tag` interface for the casing background.
  for (const event of events) {
    if (event.tags) {
      for (const tag of event.tags) {
        if (tag.id === undefined && tag.ID !== undefined) {
          tag.id = tag.ID;
        }
      }
    }
  }

  return { events, total: parseTotalCount(response.headers) };
};

/**
 * The upstream's `x-total-count`, or null when absent or unparseable. Null
 * tells a caller to fall back rather than treat an unreported total as zero.
 */
function parseTotalCount(headers: Headers): number | null {
  const raw = headers.get("x-total-count");
  if (!raw || !/^\d+$/.test(raw)) {
    return null;
  }
  const total = Number(raw);
  // A digit run long enough to exceed MAX_SAFE_INTEGER reads as Infinity, and
  // an infinite total makes `hasMore` true on every page forever.
  return Number.isSafeInteger(total) ? total : null;
}

/** One page of upstream events. The row-only form of `getTixologiEventsPage`. */
export const getTixologiEvents = async (
  params: GetTixologiEventsParams
): Promise<TixologiResumedEvent[]> =>
  (await getTixologiEventsPage(params)).events;

// Presale Codes Functions
export type PresaleCodeFiltersType = {
  page: number;
  per_page: number;
  order?: "asc" | "desc";
  code?: string;
  active?: boolean;
};

export type PresaleCodeListItem = {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  code: string;
  description?: string;
  active: boolean;
  partner_id: number;
  start_date?: string;
  end_date?: string;
  max_uses: number;
  current_uses: number;
  events?: TixologiResumedEvent[];
  ticket_types?: TixologiTicketType[];
};

export type CreatePresaleCodeRequest = {
  code: string;
  description?: string;
  active?: boolean;
  start_date?: string;
  end_date?: string;
  max_uses?: number;
};

export type UpdatePresaleCodeRequest = {
  code?: string;
  description?: string;
  active?: boolean;
  start_date?: string;
  end_date?: string;
  max_uses?: number;
};

export type AttachPresaleCodeRequest = {
  event_ids?: number[];
  ticket_type_ids?: number[];
};

export type DetachPresaleCodeRequest = {
  event_ids?: number[];
  ticket_type_ids?: number[];
};

export type EventRuleRequest = {
  event_id: number;
  max_uses: number; // -1 = unlimited
  max_uses_per_customer: number; // -1 = unlimited
};

export type TicketTypeRuleRequest = {
  ticket_type_id: number;
  max_uses: number; // -1 = unlimited
  max_uses_per_customer: number; // -1 = unlimited
};

export type SetPresaleCodeRulesRequest = {
  event_rules: EventRuleRequest[];
  ticket_type_rules: TicketTypeRuleRequest[];
};

export type PresaleCodeEventRule = {
  presale_code_id: number;
  event_id: number;
  max_uses: number;
  current_uses: number;
  max_uses_per_customer: number;
};

export type PresaleCodeTicketTypeRule = {
  presale_code_id: number;
  ticket_type_id: number;
  max_uses: number;
  current_uses: number;
  max_uses_per_customer: number;
};

export type PresaleCodeWithRulesResponse = {
  presale_code: PresaleCodeListItem;
  event_rules: PresaleCodeEventRule[];
  ticket_type_rules: PresaleCodeTicketTypeRule[];
};
